import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Target, TrendingUp, Users, Shield, Clock, CheckCircle2, Sparkles, Play, BarChart3, Mail, Menu, X } from "lucide-react";

// Video Modal Component
function VideoModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      <div 
        className="relative w-full max-w-4xl bg-slate-900 rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="video-modal-close"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        
        {/* Video Container */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe 
            src="https://www.loom.com/embed/c98988a00f0e4c288f59c8c8f2385da7?autoplay=1" 
            frameBorder="0" 
            webkitallowfullscreen="true"
            mozallowfullscreen="true"
            allowFullScreen
            allow="autoplay"
            className="absolute top-0 left-0 w-full h-full"
            title="Revolutionary Leads Demo Video"
            data-testid="video-modal-iframe"
          />
        </div>
      </div>
    </div>
  );
}

function LandingPage() {
  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Automated Discovery",
      description: "Automatically scan business formations and discover new leads every day without lifting a finger."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Verified Emails",
      description: "Get high-confidence verified email addresses for your prospects using industry-leading email enrichment."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "CRM Integration",
      description: "Seamlessly sync your leads directly to your CRM with automatic tagging and contact creation."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Collaboration",
      description: "Manage multiple users with role-based access and track lead generation across your entire team."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Data Security",
      description: "Enterprise-grade security with encrypted credentials and secure API integrations."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Scheduled Scans",
      description: "Set up automated daily, weekly, or bi-weekly scans to keep your pipeline full on autopilot."
    }
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      leads: "5 card scans/month",
      features: ["Pic-to-Lead: snap business cards into contacts", "2 free Discover trial scans", "CRM sync", "Basic support"],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Basic",
      price: "$97",
      period: "/month",
      leads: "Unlimited card scans",
      features: ["Unlimited Pic-to-Lead card scans", "Snap cards on your phone at events", "Contacts sent straight to your CRM", "Team add-on: 3 members for $50/mo"],
      cta: "Get Started",
      popular: true
    },
    {
      name: "Starter",
      price: "$199",
      period: "/month",
      leads: "100 leads/month",
      features: ["Everything in Basic", "Discover + Advanced Search", "My Leads, New Businesses & Scan History", "Team add-on: 3 members for $50/mo"],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Growth",
      price: "$399",
      period: "/month",
      leads: "250 leads/month",
      features: ["Everything in Starter", "Team: 3 members included", "Phone enrichment", "Priority support"],
      cta: "Get Started",
      popular: false,
      more: true
    },
    {
      name: "Scale",
      price: "$699",
      period: "/month",
      leads: "500 leads/month",
      features: ["Everything in Growth", "Team: 10 members included", "Meta Ads (beta)", "Dedicated support"],
      cta: "Get Started",
      popular: false,
      more: true
    }
  ];

  const steps = [
    { number: "01", title: "Connect Your CRM", description: "Link your CRM account by entering your API key and Location ID." },
    { number: "02", title: "Set Your Filters", description: "Choose industries, locations, and company types that match your ICP." },
    { number: "03", title: "Start Scanning", description: "Launch automated scans and watch qualified leads flow into your CRM." }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);

  return (
    <>
      {/* Video Modal */}
      <VideoModal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)} />
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="text-base sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Revolutionary Leads
            </span>
          </div>
          
          {/* Center Navigation Links - Desktop */}
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('features')}
              className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
              data-testid="nav-features"
            >
              Features
            </button>
            <button 
              onClick={() => scrollToSection('how-it-works')}
              className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
              data-testid="nav-how-it-works"
            >
              How It Works
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
              data-testid="nav-pricing"
            >
              Pricing
            </button>
          </div>
          
          {/* Right Side Auth Buttons - Desktop */}
          <div className="hidden sm:flex items-center gap-3">
            <Link 
              to="/login" 
              className="px-4 sm:px-5 py-2 sm:py-2.5 text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors"
              data-testid="nav-login"
            >
              Login
            </Link>
            <Link 
              to="/register" 
              className="px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-semibold rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105 transition-all"
              data-testid="nav-signup"
            >
              Sign Up Free
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="sm:hidden p-2 text-slate-600 hover:text-blue-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="landing-mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-white border-t border-slate-100 py-4 px-4">
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => scrollToSection('features')}
                className="text-left py-2 px-3 text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-left py-2 px-3 text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-left py-2 px-3 text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Pricing
              </button>
              <hr className="my-2 border-slate-100" />
              <Link 
                to="/login" 
                className="py-2 px-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Login
              </Link>
              <Link 
                to="/register" 
                className="py-3 px-4 text-sm font-semibold rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center"
              >
                Sign Up Free
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-20 left-1/4 w-48 sm:w-72 h-48 sm:h-72 bg-blue-400/20 rounded-full blur-[100px]" />
        <div className="absolute top-40 right-1/4 w-48 sm:w-72 h-48 sm:h-72 bg-purple-400/20 rounded-full blur-[100px]" />
        
        <div className="relative max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 mb-6 sm:mb-8 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 shadow-sm">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-xs sm:text-sm font-medium text-slate-700">Automated Lead Generation for Businesses</span>
          </div>
          
          {/* Main heading */}
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Find leads{" "}
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              before anyone else
            </span>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-slate-600 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
            Automatically discover new business formations, enrich with verified emails, 
            and sync directly to your CRM. Get to prospects first.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
            <Link 
              to="/register" 
              className="group w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-xl hover:shadow-blue-500/25 hover:scale-105 transition-all"
              data-testid="hero-signup"
            >
              Start Finding Leads Free
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button 
              onClick={() => setShowVideoModal(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-full border-2 border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50 transition-all"
              data-testid="hero-see-demo"
            >
              <Play className="w-4 h-4 sm:w-5 sm:h-5" />
              See How It Works
            </button>
          </div>
          
          {/* Stats */}
          <div className="mt-12 sm:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto px-4">
            {[
              { value: "50K+", label: "Leads Generated", icon: <BarChart3 className="w-5 h-5" /> },
              { value: "1,200+", label: "Active Users", icon: <Users className="w-5 h-5" /> },
              { value: "99%", label: "Email Accuracy", icon: <Mail className="w-5 h-5" /> }
            ].map((stat, i) => (
              <div key={i} className="text-center p-4 sm:p-6 rounded-2xl bg-white/60 backdrop-blur-sm border border-slate-100 shadow-sm">
                <div className="flex justify-center mb-2 text-blue-600">{stat.icon}</div>
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 px-6 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-slate-500 mb-6">Trusted by sales teams at leading companies</p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 md:gap-12 opacity-50 px-4">
            {['Real Estate Agencies', 'Marketing Firms', 'Insurance Brokers', 'Financial Services', 'SaaS Companies'].map((company, i) => (
              <span key={i} className="text-sm sm:text-lg font-semibold text-slate-400">{company}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-white to-slate-50" id="features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-blue-50 border border-blue-100">
              <Zap className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Powerful Features</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 text-slate-900 px-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything you need to{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                grow faster
              </span>
            </h2>
            <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto px-4">
              Powerful features that help you find, verify, and connect with your ideal customers.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="group relative p-5 sm:p-8 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mb-4 sm:mb-5 text-white shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" id="how-it-works">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-white/10 border border-white/20">
              <Play className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-300">Simple Process</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How it works
            </h2>
            <p className="text-slate-400 text-base sm:text-lg">
              Get started in minutes, not days
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-8 sm:gap-12">
            {steps.map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="text-6xl sm:text-8xl font-bold text-white/5 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {step.number}
                </div>
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {i + 1}
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-slate-400">
                  {step.description}
                </p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-24 right-0 translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-blue-500/50 to-transparent" />
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <Link 
              to="/register" 
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold rounded-full bg-white text-slate-900 hover:shadow-xl hover:scale-105 transition-all"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-green-50 border border-green-100">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Simple Pricing</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 text-slate-900 px-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Choose your{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                growth plan
              </span>
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              Start free. Upgrade when you're ready. Cancel anytime.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 sm:gap-8" data-testid="pricing-grid">
            {pricingPlans.filter((p) => showAllPlans || !p.more).map((plan, i) => (
              <div 
                key={plan.name}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                className={`relative flex flex-col w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] p-5 sm:p-8 rounded-2xl sm:rounded-3xl transition-all duration-300 ${plan.more ? 'animate-in fade-in slide-in-from-bottom-4 duration-500' : ''} ${
                  plan.popular 
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white shadow-2xl shadow-blue-500/25 sm:scale-105 z-10 order-first sm:order-none' 
                    : 'bg-white border-2 border-slate-100 hover:border-blue-200 hover:shadow-xl'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-yellow-400 text-slate-900 text-xs sm:text-sm font-bold shadow-lg">
                    Most Popular
                  </div>
                )}
                
                <div className="mb-4 sm:mb-6 mt-2 sm:mt-0">
                  <h3 className={`text-lg sm:text-xl font-semibold mb-2 ${plan.popular ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-3xl sm:text-5xl font-bold ${plan.popular ? 'text-white' : 'text-slate-900'}`}>{plan.price}</span>
                    <span className={plan.popular ? 'text-blue-100' : 'text-slate-500'}>{plan.period}</span>
                  </div>
                  <div className={`text-sm mt-2 font-medium ${plan.popular ? 'text-blue-100' : 'text-blue-600'}`}>{plan.leads}</div>
                </div>
                
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-1">
                  {plan.features.map((feature, j) => (
                    <li key={j} className={`flex items-center gap-2 sm:gap-3 text-sm sm:text-base ${plan.popular ? 'text-blue-50' : 'text-slate-600'}`}>
                      <CheckCircle2 className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${plan.popular ? 'text-blue-200' : 'text-green-500'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/register"
                  className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-full text-center text-sm sm:text-base font-semibold transition-all ${
                    plan.popular
                      ? 'bg-white text-blue-600 hover:shadow-lg hover:scale-105'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-8 sm:mt-10">
            <button
              onClick={() => setShowAllPlans((v) => !v)}
              data-testid="pricing-see-more-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-200 text-slate-700 font-semibold hover:border-blue-300 hover:text-blue-600 transition-colors"
            >
              {showAllPlans ? "Show fewer plans" : "See more plans"}
              <ArrowRight className={`w-4 h-4 transition-transform ${showAllPlans ? '-rotate-90' : 'rotate-90'}`} />
            </button>
          </div>
          
          <p className="text-center mt-6 sm:mt-8 text-slate-500 text-sm">
            Need teammates on Basic or Starter? Add the Team add-on — 3 members for $50/month.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 sm:p-12 md:p-16 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-2xl shadow-blue-500/25 overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-white/10 rounded-full blur-[60px]" />
            <div className="absolute bottom-0 left-0 w-32 sm:w-48 h-32 sm:h-48 bg-purple-400/20 rounded-full blur-[60px]" />
            
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ready to revolutionize your lead gen?
              </h2>
              <p className="text-blue-100 text-base sm:text-lg mb-6 sm:mb-8 max-w-xl mx-auto">
                Join thousands of sales teams finding and closing deals faster with automated lead discovery.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link 
                  to="/register" 
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-full bg-white text-blue-600 hover:shadow-xl hover:scale-105 transition-all"
                  data-testid="cta-signup"
                >
                  Sign Up Free
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </Link>
                <Link 
                  to="/login" 
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-full border-2 border-white/30 text-white hover:bg-white/10 transition-all"
                  data-testid="cta-login"
                >
                  Login to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-6 sm:gap-8 pb-6 sm:pb-8 border-b border-slate-800 md:flex-row md:justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold">Revolutionary Leads</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm">
              <button onClick={() => scrollToSection('features')} className="text-slate-400 hover:text-white transition-colors">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="text-slate-400 hover:text-white transition-colors">Pricing</button>
              <Link to="/login" className="text-slate-400 hover:text-white transition-colors">Login</Link>
              <Link to="/register" className="text-slate-400 hover:text-white transition-colors">Sign Up</Link>
            </div>
          </div>
          <div className="pt-6 sm:pt-8 flex flex-col items-center gap-3 sm:gap-4 md:flex-row md:justify-between">
            <p className="text-slate-500 text-xs sm:text-sm text-center">
              © 2026 Revolutionary Leads. All rights reserved.
            </p>
            <p className="text-slate-500 text-xs sm:text-sm text-center">
              Powered by <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-semibold">Revo Digital</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}

export default LandingPage;
